// AuthContext.js
import { createContext, useContext, useState, useEffect } from 'react';
import { GRAPHQL_ENDPOINT } from '../apollo/client';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [auth, setAuth] = useState({ status: 'loading', user: null }); // status: 'loading', 'unauthenticated', 'authenticated'

  // Cache auth result to avoid repeated API calls
  const authCacheKey = 'auth_cache';
  const authCacheDuration = 5 * 60 * 1000; // 5 minutes

  // Function to invalidate auth cache (useful for login/logout)
  const invalidateAuthCache = () => {
    localStorage.removeItem(authCacheKey);
  };

  async function checkToken() {
        const token = localStorage.getItem('token'); // Revert to using the original token
        if (!token) {
            return false;
        }

        try {
            const response = await fetch(GRAPHQL_ENDPOINT + '/auth/check', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token: token,
                }),
            });

            if (!response.ok) {
                throw new Error('Token check failed');
            }
            const data = await response.json();
            return data;
        } catch (error) {
            console.error('Error checking token:', error);
            return false;
        }
      }
  
  // Move validate to top-level so it can be called from anywhere
  async function validate() {
    const result = await checkToken();
    if (result && result.token && result.role) {
      // Token is valid (token field present), set authenticated state with user data

      setAuth({ status: 'authenticated', user: { role: result.role, username: result.username } });
    } else {
      // Token is invalid or missing, set unauthenticated state
      setAuth({ status: 'unauthenticated', user: null });
    }
  }

  useEffect(() => {
    // Listen for token changes (login/logout) and re-validate
    function handleStorageChange(e) {
      if (e.key === 'token') {
        invalidateAuthCache();
        setAuth({ status: 'loading', user: null });
        validate();
      }
    }
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    validate();
  }, []);

  const contextValue = {
    ...auth,
    invalidateAuthCache
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
